import { Injectable, OnDestroy } from '@angular/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, finalize, map, takeUntil, tap } from 'rxjs/operators';
import { RequestDelete, ResponseDelete } from '../../../../modules/fwcore/models/messages';
import { Deal, RequestDeal, RequestDealSet, ResponseDeal, ResponseDealSet } from '../../models';
import { DealsService } from '../deals.service';

@Injectable({
  providedIn: 'root'
})
export class DealsGridService extends BehaviorSubject<GridDataResult> implements OnDestroy {

  //-------------------------- public properties --------------------

  public loading: boolean = true;

  //-------------------------- Internal properties  ------------------

  protected take: number = 5;
  protected destroy$ = new Subject();

  //-------------------------- constructor  --------------------------

  constructor(protected dataService: DealsService) {

    const emptyDataResult = {
      data: [],
      total: 0
    }

    super(emptyDataResult);
  }

  //----------------------------------- ng  --------------------------

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  //-------------------------- public methods ------------------------
    
  query(req: RequestDeal) {

    this.fetch(req)
      .subscribe((x) => super.next(x));
  }

  /**
   * Clears the cached grid result and flags the grid as loading.
   * Useful when a host component is about to issue a new query and
   * wants to avoid briefly rendering the previous page's rows.
   */
  public reset(): void {

    this.loading = true;
    super.next({ data: [], total: 0 });
  }

  queryAll(): Observable<GridDataResult> {

    const req: RequestDeal = {

    };

    return this.fetch(req);
  }

  fetch(req: RequestDeal): Observable<GridDataResult> {

    this.take = req.take ?? 0;

    this.loading = true;

    return this.dataService.getData$<RequestDeal, ResponseDeal>(req, this.dataService.endPointDeals)
      .pipe(
        map(
          (response) =>
            <GridDataResult>{
               data: response.data
              , total: response.total
            }
        )
        , finalize(() => (this.loading = false))
      );
    
  }

  save(data: Deal[]): Observable<boolean> {

    return this.saveApi(data)
      .pipe(
          map( modifiedData => {
            return this.syncModificationsToLocal(modifiedData);
          })
        , tap(x => { super.next(x); })
        , map(() => true) // Return true indicating successful save
        , catchError(() => of(false)) // Handle error and return false indicating unsuccessful save
      );
  }

  remove(data: Deal) {

    let req: RequestDelete = new RequestDelete();
    
    req.id = data.id!;

    this.dataService.deleteData$<RequestDelete, ResponseDelete>(req, this.dataService.endPointDeals)
      .pipe(
          takeUntil(this.destroy$)
        , finalize(() => this.loading = false)
      )
      .subscribe((res) => {
        if (res.success) {
          this.removeLocalItemById(data.id!);
        }
      });
  }

  //--------------------------- internal functions  -------------------
 
  protected saveApi(data: Deal[]): Observable<Deal[]> {

    let req: RequestDealSet = new RequestDealSet();
    req.data = data;

    return this.dataService.setData$<RequestDealSet, ResponseDealSet>(req, this.dataService.endPointDeals)
      .pipe(
        map((response : ResponseDealSet) => response.data ?? [])
          , finalize(() => (this.loading = false))
        );

  }

  protected syncModificationsToLocal(modifiedItems: Deal[]): GridDataResult {

    let totalCount = this.value.total;

    let pos: number = 0;
    let item: Deal = {};
    let itemMod: Deal = {};

    let newData: Deal[] = [];

    // Create a copy of our array to manipulate and return
    for (pos = 0; pos < this.value.data.length; pos++) {
      newData.push(this.value.data[pos]);
    }

    // Get each of the modified items
    for (let j = modifiedItems.length - 1; j >= 0; j--) {

      itemMod = modifiedItems[j];
      let found = false;

      // See if they exist in our data already
      for (let i = newData.length - 1; i >= 0; i--) {

        item = newData[i];

        if (item.id == itemMod.id) {

          // match was found so substitute the modification
          newData.splice(i, 1, itemMod);
          found = true;
          break;
        }
      }

      if (found == false) {
        // Not found so insert the new item at the top of the list - user can see addition then
        newData.splice(0, 0, itemMod);
        totalCount += 1;

        // Remove items from the end of the array if we now overlap the page size due to the addition
        if (newData.length > this.take)
          newData.splice(this.take, 1);
      }
    }

    // Return the updated data array
    return { data: newData, total: totalCount };
  }
  
  protected removeLocalItemById(id: string) {

    this.value.data.forEach((item, index) => {
      if (item.id == id) {
        this.value.data.splice(index, 1);
        this.value.total -= 1;
      }
    });
  }
}
